// src/pages/Question/QuestionPane.jsx
import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@root/firebase';
import { formatTime } from '@utils/Date';
import BotPrompt from './BotPrompt';
import { getCurrentTime } from '@utils/TimeHelpers';
import { questionCategories } from '@utils/Index';

const QuestionPane = ({
  currentQuestion,
  setCurrentQuestion,
  userProfiles,
  globalData,
  defaultAvatarUrl,
  messageInput,
  setMessageInput,
  handleReplyToQuestion,
  handleAssignAdmin,
  handleReopenQuestion,
  globalUser,
  setSelectedQuestion, // parent: set modal "name"
  setSelectedQuestionId, // parent: set modal "id"

  // ask-flow props (rendered in-pane when there's no current thread)
  askQuestion,
  askQuestionStep,
  setAskQuestionStep,
  dogs,
  selectedCategory,
  setSelectedCategory,
  selectedDog,
  setSelectedDog,
  handleSaveQuestion,
  isLoading,
}) => {
  const [localQuestion, setLocalQuestion] = useState(currentQuestion);

  // Mirror live updates for the selected thread
  useEffect(() => {
    setLocalQuestion(currentQuestion || null);
    if (!currentQuestion) return;

    const ref = doc(db, 'questions', currentQuestion.id);
    const unsub = onSnapshot(ref, (snap) => {
      if (snap.exists()) {
        setLocalQuestion({ id: snap.id, ...snap.data() });
      } else {
        setLocalQuestion(null);
        setCurrentQuestion(null);
      }
    });

    return () => unsub();
  }, [currentQuestion, setCurrentQuestion]);

  return (
    // One card that acts as a vertical layout (header, scroll area, footer)
    <div className='chat-card h-100 d-flex flex-column'>
      {/* Header (fixed) */}
      <div className='chat-header d-flex justify-content-between align-items-center px-4 pt-4 pb-3 border-bottom'>
        <div className='d-flex align-items-center'>
          <div className='chat-img me-3'>
            <img
              src={defaultAvatarUrl}
              alt='agent'
              className='rounded-circle'
              style={{ width: 48, height: 48, objectFit: 'cover' }}
            />
          </div>
          <div>
            <h4 className='fs-20 font-w700 m-0'>
              {localQuestion?.adminId ? 'Assigned Admin' : 'Agent Unassigned'}
            </h4>
            <span className='text-muted'>
              {localQuestion?.status === 'open'
                ? 'Open'
                : localQuestion
                  ? 'Closed'
                  : 'New'}
            </span>
          </div>
        </div>

        <div className='dropdown ms-2'>
          <button
            className='btn btn-link'
            type='button'
            data-bs-toggle='dropdown'
            aria-expanded='false'
          >
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <circle cx='12.5' cy='3.5' r='2.5' fill='#A5A5A5' />
              <circle cx='12.5' cy='11.5' r='2.5' fill='#A5A5A5' />
              <circle cx='12.5' cy='19.5' r='2.5' fill='#A5A5A5' />
            </svg>
          </button>

          <ul className='dropdown-menu dropdown-menu-end'>
            {!!localQuestion &&
              globalData?.isAdmin &&
              !localQuestion.adminId && (
                <li>
                  <button
                    type='button'
                    className='dropdown-item'
                    onClick={() => {
                      setSelectedQuestionId(localQuestion.id);
                    }}
                    data-bs-toggle='modal'
                    data-bs-target='#confirmAssignAdmin'
                  >
                    Assign to Me
                  </button>
                </li>
              )}
            <li>
              <button
                type='button'
                className='dropdown-item'
                onClick={() => {
                  setSelectedQuestionId(localQuestion.id);
                }}
                data-bs-toggle='modal'
                data-bs-target='#confirmReOpenQuestion'
              >
                Re-Open
              </button>
            </li>
            <li>
              <button
                type='button'
                className='dropdown-item'
                onClick={() => {
                  setSelectedQuestionId(localQuestion.id);
                }}
                data-bs-toggle='modal'
                data-bs-target='#confirmCloseQuestion'
              >
                Close
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Scrollable messages area */}
      <div className='chat-scroll flex-grow-1 overflow-auto px-4 py-3'>
        {/* Ask flow visible when no active thread */}
        {!localQuestion && askQuestion && (
          <>
            <BotPrompt
              defaultAvatarUrl={defaultAvatarUrl}
              time={getCurrentTime()}
              message='Is your query related to any of the following?'
              options={questionCategories}
              onOptionSelect={(cat) => {
                setSelectedCategory(cat);
                setAskQuestionStep(2);
              }}
            />
            {askQuestionStep >= 2 && (
              <BotPrompt
                defaultAvatarUrl={defaultAvatarUrl}
                time={getCurrentTime()}
                message='Please select your dog(s) in question:'
                options={dogs.map((d) => ({ value: d.name, label: d.name }))}
                onOptionSelect={(dog) => {
                  setSelectedDog(dog);
                  setAskQuestionStep(3);
                }}
              />
            )}
            {askQuestionStep >= 3 && (
              <BotPrompt
                defaultAvatarUrl={defaultAvatarUrl}
                time={getCurrentTime()}
                message={`Great, how can I help with ${selectedDog} regarding ${selectedCategory}?`}
              />
            )}
          </>
        )}

        {/* Normal thread */}
        {!!localQuestion &&
          localQuestion.messages?.map((msg, idx) => {
            const isMine = msg.senderId === globalUser?.uid;
            const time = msg.timestamp?.toDate?.() || new Date(msg.timestamp);
            const senderProfile = userProfiles[msg.senderId] || {};
            const senderAvatar = senderProfile.avatar || defaultAvatarUrl;
            const senderName = senderProfile.name || 'Unknown User';

            if (!isMine) {
              return (
                <div
                  key={idx}
                  className='media my-4 justify-content-start align-items-start'
                >
                  <div className='image-box me-sm-4 me-2'>
                    <img
                      src={senderAvatar}
                      alt={senderName}
                      className='rounded-circle img-1'
                      style={{ width: 48, height: 48, objectFit: 'cover' }}
                    />
                    <span className='active1' />
                  </div>
                  <div className='message-received'>
                    <p className='mb-1'>{msg.text}</p>
                    <span className='fs-12'>{formatTime(time)}</span>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={idx}
                className='media mb-4 justify-content-end align-items-end'
              >
                <div className='message-sent'>
                  <p className='mb-1'>{msg.text}</p>
                  <span className='fs-12'>{formatTime(time)}</span>
                </div>
                <div className='image-box ms-sm-4 ms-2 mb-4'>
                  <img
                    src={globalData?.avatar || defaultAvatarUrl}
                    alt={globalData?.name || 'Me'}
                    className='rounded-circle img-1'
                    style={{ width: 48, height: 48, objectFit: 'cover' }}
                  />
                  <span className='active' />
                </div>
              </div>
            );
          })}
      </div>

      {/* Footer / Composer (fixed) */}
      <div className='chat-footer border-top p-3'>
        {!!localQuestion && localQuestion.status === 'closed' && (
          <button
            className='btn btn-primary me-2 mb-2'
            onClick={() => handleReopenQuestion(localQuestion.id)}
          >
            Reopen Question
          </button>
        )}

        <div className='d-flex align-items-center gap-2'>
          <textarea
            className='form-control'
            rows='2'
            placeholder={
              askQuestion && !localQuestion
                ? 'Enter your question...'
                : 'Type your reply...'
            }
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            style={{ resize: 'none' }}
          />

          {askQuestion && !localQuestion ? (
            <button
              className='btn btn-primary'
              onClick={handleSaveQuestion}
              disabled={isLoading || askQuestionStep < 3}
            >
              {isLoading ? <i className='mdi mdi-loading mdi-spin' /> : 'Ask'}
            </button>
          ) : (
            !!localQuestion && (
              <button
                className='btn btn-primary'
                onClick={() =>
                  handleReplyToQuestion(localQuestion.id, messageInput)
                }
              >
                SEND
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionPane;
